import React, { useState, useEffect } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { StatsCard } from '../components/ui/stats-card';
import { Skeleton } from '../components/ui/skeleton';
import {
  Search,
  Filter,
  Download,
  Eye,
  Edit,
  Trash2,
  UserCheck,
  UserX,
  Bell,
  ChevronLeft,
  ChevronRight,
  Users,
  UserPlus,
  CheckCircle,
  Clock,
  Smartphone,
  ShoppingCart,
  TrendingUp,
  MapPin,
  Mail,
  Phone
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '../components/ui/table';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { AdminMobileUserAPI, EnhancedMobileUser, MobileUserStats } from '../lib/api';
import { toast } from '../components/ui/use-toast';
import { useConfirm } from '../components/ui/confirm-dialog';
import { StatusBadge } from '../components/ui/status-badge';
import { RowActions } from '../components/ui/row-actions';
import { Pagination } from '../components/ui/pagination';
import { formatCurrency, formatDateShort, formatTime } from '../lib/utils';
import MobileUserDetailModal from '../components/mobile-users/MobileUserDetailModal';
import MobileUserEditModal from '../components/mobile-users/MobileUserEditModal';
import SendNotificationModal from '../components/mobile-users/SendNotificationModal';

export default function MobileUsersPage() {
  const confirm = useConfirm();
  const [users, setUsers] = useState<EnhancedMobileUser[]>([]);
  const [selectedUser, setSelectedUser] = useState<EnhancedMobileUser | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isDeleting, setIsDeleting] = useState<string | null>(null);
  const [isToggling, setIsToggling] = useState<string | null>(null);
  const [editingUser, setEditingUser] = useState<EnhancedMobileUser | null>(null);
  const [notificationUser, setNotificationUser] = useState<EnhancedMobileUser | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalUsers, setTotalUsers] = useState(0);
  const [userStats, setUserStats] = useState<MobileUserStats | null>(null);
  const [activeStatusFilter, setActiveStatusFilter] = useState<string>('all');
  const [verificationStatusFilter, setVerificationStatusFilter] = useState<string>('all');
  const [sortBy, setSortBy] = useState<string>('createdAt');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const pageSize = 10;

  // Debounce search query
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Fetch users on component mount or when search/page/filters change
  useEffect(() => {
    fetchUsers();
  }, [currentPage, debouncedSearchQuery, activeStatusFilter, verificationStatusFilter, sortBy, sortOrder]);

  // Fetch user statistics on component mount
  useEffect(() => {
    fetchUserStatistics();
  }, []);

  const fetchUserStatistics = async () => {
    try {
      const response = await AdminMobileUserAPI.getMobileUserStatistics({
        period: '30d'
      });

      if (response.success) {
        setUserStats(response.data);
      } else {
        console.error('Failed to fetch user statistics:', response.message);
      }
    } catch (error) {
      console.error('Error fetching user statistics:', error);
    }
  };

  const fetchUsers = async () => {
    try {
      setIsLoading(true);
      const response = await AdminMobileUserAPI.getAllMobileUsers({
        search: debouncedSearchQuery || undefined,
        isActive: activeStatusFilter !== 'all' ? activeStatusFilter === 'active' : undefined,
        isPhoneVerified: verificationStatusFilter !== 'all' ? verificationStatusFilter === 'verified' : undefined,
        page: currentPage,
        limit: pageSize,
        sortBy: sortBy,
        sortOrder: sortOrder,
        includeStats: true
      });

      if (response.success) {
        setUsers(response.data.users);
        setTotalPages(response.data.pagination.totalPages);
        setTotalUsers(response.data.pagination.totalUsers);
      } else {
        toast({
          title: "Error",
          description: "Failed to fetch mobile users",
          variant: "destructive"
        });
        setUsers([]);
      }
    } catch (error) {
      console.error("Error fetching mobile users:", error);
      toast({
        title: "Error",
        description: "Failed to fetch mobile users. Please try again.",
        variant: "destructive"
      });
      setUsers([]);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle user deletion
  const handleDeleteUser = async (userId: string) => {
    const ok = await confirm({
      title: 'Delete this user?',
      description: 'This action cannot be undone.',
      confirmText: 'Delete',
      destructive: true,
    });
    if (!ok) return;

    try {
      setIsDeleting(userId);
      const response = await AdminMobileUserAPI.deleteMobileUser(userId, {
        permanent: false,
        reason: "Deleted by admin"
      });

      if (response.success) {
        toast({
          title: "Success",
          description: "User deleted successfully"
        });

        setUsers(prev => prev.filter(u => u._id !== userId));
      } else {
        toast({
          title: "Error",
          description: response.message || "Failed to delete user",
          variant: "destructive"
        });
      }
    } catch (error) {
      console.error("Error deleting user:", error);
      toast({
        title: "Error",
        description: "Failed to delete user. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsDeleting(null);
    }
  };

  // Handle toggling user active status
  const handleToggleActive = async (userId: string) => {
    try {
      setIsToggling(userId);
      const user = users.find(u => u._id === userId);
      if (!user) return;

      const response = await AdminMobileUserAPI.updateMobileUser(userId, {
        isActive: !user.isActive,
        adminNotes: `Status changed to ${!user.isActive ? 'active' : 'inactive'} by admin`
      });

      if (response.success) {
        toast({
          title: "Success",
          description: `User ${response.data.isActive ? 'activated' : 'deactivated'} successfully`
        });

        setUsers(prev => prev.map(u => u._id === userId ? response.data : u));

        if (selectedUser && selectedUser._id === userId) {
          setSelectedUser(response.data);
        }
      } else {
        toast({
          title: "Error",
          description: response.message || "Failed to update user status",
          variant: "destructive"
        });
      }
    } catch (error) {
      console.error("Error toggling user status:", error);
      toast({
        title: "Error",
        description: "Failed to update user status. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsToggling(null);
    }
  };

  // Handle viewing user details
  const handleViewUser = async (userId: string) => {
    try {
      setIsLoading(true);
      const response = await AdminMobileUserAPI.getMobileUserById(userId);

      if (response.success) {
        setSelectedUser(response.data);
      } else {
        toast({
          title: "Error",
          description: response.message || "Failed to fetch user details",
          variant: "destructive"
        });
      }
    } catch (error) {
      console.error("Error fetching user details:", error);
      toast({
        title: "Error",
        description: "Failed to fetch user details. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Handle editing user
  const handleEditUser = (user: EnhancedMobileUser) => {
    setEditingUser(user);
  };

  // Handle sending notification
  const handleSendNotification = (user: EnhancedMobileUser) => {
    setNotificationUser(user);
  };

  // Handle search
  const handleSearch = () => {
    setCurrentPage(1);
    fetchUsers();
  };

  // Handle key press in search input
  const handleSearchKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  // Handle pagination
  const goToPage = (page: number) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  // Get key metrics for the top cards
  const keyMetrics = [
    {
      title: 'Total Users',
      value: userStats?.overview?.totalUsers || totalUsers,
      icon: <Users className="h-6 w-6" />,
      color: 'text-blue-600',
      bgColor: 'bg-blue-50',
    },
    {
      title: 'Active Users',
      value: userStats?.overview?.activeUsers || users.filter(u => u.isActive).length,
      icon: <UserCheck className="h-6 w-6" />,
      color: 'text-green-600',
      bgColor: 'bg-green-50',
    },
    {
      title: 'Verified Users',
      value: userStats?.overview?.verifiedUsers || users.filter(u => u.isPhoneVerified).length,
      icon: <CheckCircle className="h-6 w-6" />,
      color: 'text-purple-600',
      bgColor: 'bg-purple-50',
    },
    {
      title: 'Total Revenue',
      value: userStats?.orders?.totalRevenue ? formatCurrency(userStats.orders.totalRevenue) : '₹0',
      icon: <TrendingUp className="h-6 w-6" />,
      color: 'text-orange-600',
      bgColor: 'bg-orange-50',
    },
  ];

  return (
    <div className="space-y-4 sm:space-y-8 px-1 sm:px-0">
      {/* Header */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">Mobile Users</h1>
          <p className="text-muted-foreground text-sm sm:text-base mt-1 sm:mt-2">Manage mobile app users and their activities</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" className="flex-1 sm:flex-none">
            <Filter className="h-4 w-4 mr-2" />
            <span className="hidden xs:inline">Advanced </span>Filter
          </Button>
          <Button variant="outline" size="sm" className="flex-1 sm:flex-none">
            <Download className="h-4 w-4 mr-2" />
            Export
          </Button>
        </div>
      </div>

      {/* Key Metrics Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-6">
        {keyMetrics.map((metric, index) => (
          <StatsCard
            key={index}
            title={metric.title}
            value={typeof metric.value === 'string' ? metric.value : metric.value}
            icon={metric.icon}
            iconClassName={`${metric.bgColor} ${metric.color}`}
          />
        ))}
      </div>

      {/* Search and Actions */}
      <Card>
        <CardContent className="p-6">
          <div className="flex flex-col sm:flex-row gap-4 justify-between items-center">
            <div className="relative w-full sm:w-auto">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search by name, email, or phone..."
                className="pl-10 w-full sm:w-[350px]"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyPress={handleSearchKeyPress}
              />
            </div>
            <Button onClick={handleSearch} className="btn-primary-enhanced">
              <Search className="h-4 w-4 mr-2" />
              Search
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Enhanced filters */}
      <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4">
        <div className="space-y-2">
          <Label htmlFor="active-filter">Active Status</Label>
          <Select value={activeStatusFilter} onValueChange={setActiveStatusFilter}>
            <SelectTrigger id="active-filter">
              <SelectValue placeholder="All" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Users</SelectItem>
              <SelectItem value="active">Active Only</SelectItem>
              <SelectItem value="inactive">Inactive Only</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <Label htmlFor="verification-filter">Verification Status</Label>
          <Select value={verificationStatusFilter} onValueChange={setVerificationStatusFilter}>
            <SelectTrigger id="verification-filter">
              <SelectValue placeholder="All" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Users</SelectItem>
              <SelectItem value="verified">Verified Only</SelectItem>
              <SelectItem value="unverified">Unverified Only</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <Label htmlFor="sort-by">Sort By</Label>
          <Select value={sortBy} onValueChange={setSortBy}>
            <SelectTrigger id="sort-by">
              <SelectValue placeholder="Created Date" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="createdAt">Registration Date</SelectItem>
              <SelectItem value="firstName">Name</SelectItem>
              <SelectItem value="totalOrders">Total Orders</SelectItem>
              <SelectItem value="totalSpent">Total Spent</SelectItem>
              <SelectItem value="lastLogin">Last Login</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <Label htmlFor="sort-order">Sort Order</Label>
          <Select value={sortOrder} onValueChange={(value: 'asc' | 'desc') => setSortOrder(value)}>
            <SelectTrigger id="sort-order">
              <SelectValue placeholder="Descending" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="desc">Descending</SelectItem>
              <SelectItem value="asc">Ascending</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Users Table */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span>Mobile Users</span>
            <span className="text-sm font-normal text-muted-foreground">
              {totalUsers} total users
            </span>
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {/* Desktop table */}
          <div className="hidden md:block overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="border-b">
                  <TableHead className="font-semibold">User</TableHead>
                  <TableHead className="font-semibold">Contact</TableHead>
                  <TableHead className="font-semibold">Status</TableHead>
                  <TableHead className="font-semibold">Orders</TableHead>
                  <TableHead className="font-semibold">Total Spent</TableHead>
                  <TableHead className="font-semibold">Last Login</TableHead>
                  <TableHead className="font-semibold">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoading ? (
                  Array.from({ length: pageSize }).map((_, index) => (
                    <TableRow key={index}>
                      <TableCell><Skeleton className="h-12 w-full" /></TableCell>
                      <TableCell><Skeleton className="h-4 w-32" /></TableCell>
                      <TableCell><Skeleton className="h-6 w-16" /></TableCell>
                      <TableCell><Skeleton className="h-4 w-8" /></TableCell>
                      <TableCell><Skeleton className="h-4 w-20" /></TableCell>
                      <TableCell><Skeleton className="h-4 w-24" /></TableCell>
                      <TableCell><Skeleton className="h-8 w-32" /></TableCell>
                    </TableRow>
                  ))
                ) : (users && users.length === 0) ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-12">
                      <div className="flex flex-col items-center justify-center text-muted-foreground">
                        <Users className="h-12 w-12 mb-4 opacity-50" />
                        <p className="text-lg font-medium">No users found</p>
                        <p className="text-sm">
                          {searchQuery || activeStatusFilter !== 'all' || verificationStatusFilter !== 'all'
                            ? 'Try adjusting your filters or search terms'
                            : 'Users will appear here once they register'}
                        </p>
                      </div>
                    </TableCell>
                  </TableRow>
                ) : (
                  users.map((user) => (
                    <TableRow key={user._id} className="hover:bg-accent/50">
                      <TableCell>
                        <div className="flex items-center gap-3">
                          {user.profilePicture ? (
                            <img
                              src={user.profilePicture}
                              alt={user.firstName}
                              className="w-10 h-10 rounded-full object-cover"
                            />
                          ) : (
                            <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                              <Users className="h-5 w-5 text-primary" />
                            </div>
                          )}
                          <div>
                            <div className="font-medium">
                              {user.firstName} {user.lastName || ''}
                            </div>
                            {user.createdAt && (
                              <div className="text-sm text-muted-foreground">
                                Joined {new Date(user.createdAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
                              </div>
                            )}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="space-y-1">
                          {user.email && (
                            <div className="flex items-center gap-1 text-sm">
                              <Mail className="h-3 w-3" />
                              {user.email}
                            </div>
                          )}
                          <div className="flex items-center gap-1 text-sm">
                            <Phone className="h-3 w-3" />
                            {user.phoneNumber || user.phone}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-col items-start gap-1">
                          <StatusBadge status={user.isActive ? 'active' : 'inactive'} />
                          <StatusBadge status={user.isPhoneVerified ? 'verified' : 'unverified'} />
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          <ShoppingCart className="h-4 w-4 text-muted-foreground" />
                          <span className="font-medium">{user.totalOrders || 0}</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <span className="font-semibold text-green-600">
                          {formatCurrency(user.totalSpent || 0)}
                        </span>
                      </TableCell>
                      <TableCell>
                        <div className="text-sm">
                          {user.lastLogin ? (
                            <>
                              <div>{formatDateShort(user.lastLogin)}</div>
                              <div className="text-muted-foreground">{formatTime(user.lastLogin)}</div>
                            </>
                          ) : (
                            <span className="text-muted-foreground">Never</span>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <RowActions
                          actions={[
                            { label: 'View', icon: <Eye className="h-4 w-4" />, onClick: () => handleViewUser(user._id) },
                            { label: 'Edit', icon: <Edit className="h-4 w-4" />, onClick: () => handleEditUser(user) },
                            { label: 'Send notification', icon: <Bell className="h-4 w-4" />, onClick: () => handleSendNotification(user) },
                            {
                              label: user.isActive ? 'Deactivate' : 'Activate',
                              icon: user.isActive ? <UserX className="h-4 w-4" /> : <UserCheck className="h-4 w-4" />,
                              onClick: () => handleToggleActive(user._id),
                              disabled: isToggling === user._id,
                            },
                            {
                              label: 'Delete',
                              icon: <Trash2 className="h-4 w-4" />,
                              onClick: () => handleDeleteUser(user._id),
                              disabled: isDeleting === user._id,
                              destructive: true,
                              separatorBefore: true,
                            },
                          ]}
                        />
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>

          {/* Mobile cards */}
          <div className="md:hidden divide-y">
            {isLoading ? (
              Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="p-4 space-y-2">
                  <Skeleton className="h-5 w-1/2" />
                  <Skeleton className="h-4 w-2/3" />
                  <Skeleton className="h-4 w-1/3" />
                </div>
              ))
            ) : (users && users.length === 0) ? (
              <div className="flex flex-col items-center justify-center text-muted-foreground py-12">
                <Users className="h-12 w-12 mb-4 opacity-50" />
                <p className="text-lg font-medium">No users found</p>
              </div>
            ) : (
              users.map((user) => (
                <div key={user._id} className="p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex items-center gap-3 min-w-0">
                      {user.profilePicture ? (
                        <img src={user.profilePicture} alt={user.firstName} className="w-10 h-10 rounded-full object-cover" />
                      ) : (
                        <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                          <Users className="h-5 w-5 text-primary" />
                        </div>
                      )}
                      <div className="min-w-0">
                        <div className="font-medium truncate">{user.firstName} {user.lastName || ''}</div>
                        <div className="text-sm text-muted-foreground truncate flex items-center gap-1">
                          <Phone className="h-3 w-3" />{user.phoneNumber || user.phone}
                        </div>
                      </div>
                    </div>
                    <RowActions
                      actions={[
                        { label: 'View', icon: <Eye className="h-4 w-4" />, onClick: () => handleViewUser(user._id) },
                        { label: 'Edit', icon: <Edit className="h-4 w-4" />, onClick: () => handleEditUser(user) },
                        { label: 'Send notification', icon: <Bell className="h-4 w-4" />, onClick: () => handleSendNotification(user) },
                        {
                          label: user.isActive ? 'Deactivate' : 'Activate',
                          icon: user.isActive ? <UserX className="h-4 w-4" /> : <UserCheck className="h-4 w-4" />,
                          onClick: () => handleToggleActive(user._id),
                          disabled: isToggling === user._id,
                        },
                        {
                          label: 'Delete', icon: <Trash2 className="h-4 w-4" />,
                          onClick: () => handleDeleteUser(user._id),
                          disabled: isDeleting === user._id, destructive: true, separatorBefore: true,
                        },
                      ]}
                    />
                  </div>
                  <div className="mt-3 flex flex-wrap items-center gap-2">
                    <StatusBadge status={user.isActive ? 'active' : 'inactive'} />
                    <StatusBadge status={user.isPhoneVerified ? 'verified' : 'unverified'} />
                    <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
                      <ShoppingCart className="h-3 w-3" />{user.totalOrders || 0} orders
                    </span>
                    <span className="text-xs font-semibold text-green-600">{formatCurrency(user.totalSpent || 0)}</span>
                  </div>
                </div>
              ))
            )}
          </div>
        </CardContent>
      </Card>

      {/* Pagination */}
      {!isLoading && users.length > 0 && (
        <Card>
          <CardContent className="p-4">
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={goToPage}
              summary={`Showing ${(currentPage - 1) * pageSize + 1}–${Math.min(currentPage * pageSize, totalUsers)} of ${totalUsers} users`}
            />
          </CardContent>
        </Card>
      )}

      {/* Modals */}
      {selectedUser && (
        <MobileUserDetailModal
          isOpen={!!selectedUser}
          onClose={() => setSelectedUser(null)}
          user={selectedUser}
        />
      )}

      {editingUser && (
        <MobileUserEditModal
          isOpen={!!editingUser}
          onClose={() => setEditingUser(null)}
          onSuccess={() => {
            fetchUsers();
            fetchUserStatistics();
          }}
          user={editingUser}
        />
      )}

      {notificationUser && (
        <SendNotificationModal
          isOpen={!!notificationUser}
          onClose={() => setNotificationUser(null)}
          onSuccess={() => {
            toast({
              title: "Success",
              description: "Notification sent successfully"
            });
          }}
          user={notificationUser}
        />
      )}
    </div>
  );
}